import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, XCircle } from "lucide-react";
import AuthLayout, { authStyles } from "@/components/AuthLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      toast.error("Invalid or missing reset token");
    }
  }, [token]);

  const validatePassword = (password) => password.length >= 6;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePassword(newPassword)) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      toast.success("Password reset successful!");
      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to reset password. Please try again.";
      toast.error(errorMsg);
      if (errorMsg.includes("expired") || errorMsg.includes("Invalid")) {
        setTokenValid(false);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <AuthLayout>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            padding: "30px 20px",
            border: "1px solid rgba(239,68,68,.28)",
            background: "rgba(239,68,68,.07)",
            borderRadius: 16,
          }}
        >
          <span style={{ width: 54, height: 54, borderRadius: 15, background: "rgba(239,68,68,.16)", color: "#f87171", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
            <XCircle className="w-[26px] h-[26px]" />
          </span>
          <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 17, color: "#fff" }}>Invalid reset link</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.6)", marginTop: 6, lineHeight: 1.55 }}>
            This link is invalid or has expired. Reset links expire after 1 hour for security.
          </div>
        </div>
        <Link to="/forgot-password" className="auth-primary" style={{ ...authStyles.primaryBtn, display: "block", textAlign: "center", textDecoration: "none", marginTop: 18 }}>
          Request a new link
        </Link>
        <div style={{ textAlign: "center", fontSize: 13, color: "rgba(255,255,255,.55)", marginTop: 16 }}>
          <Link to="/login" className="auth-link" style={{ fontWeight: 600 }}>&larr; Back to sign in</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="auth-head" style={authStyles.heading}>Reset password</div>
      <div className="auth-head" style={{ ...authStyles.sub, marginBottom: 24 }}>Create your new password.</div>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        <div>
          <span style={authStyles.label}>New password</span>
          <div style={{ position: "relative" }}>
            <input
              className="auth-input"
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
              style={{ ...authStyles.input, paddingRight: 44 }}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(255,255,255,.45)", cursor: "pointer", display: "flex" }}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <span style={authStyles.label}>Confirm password</span>
          <div style={{ position: "relative" }}>
            <input
              className="auth-input"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              style={{ ...authStyles.input, paddingRight: 44 }}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(255,255,255,.45)", cursor: "pointer", display: "flex" }}
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <button type="submit" disabled={loading} className="auth-primary" style={{ ...authStyles.primaryBtn, marginTop: 4, opacity: loading ? 0.7 : 1 }}>
          {loading ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-t-transparent" style={{ borderColor: "#0C1120", borderTopColor: "transparent" }}></span>
              Resetting…
            </span>
          ) : "Reset password"}
        </button>
      </form>

      <div style={{ textAlign: "center", fontSize: 13, color: "rgba(255,255,255,.55)", marginTop: 20 }}>
        <Link to="/login" className="auth-link" style={{ fontWeight: 600 }}>&larr; Back to sign in</Link>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
